import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Star } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useMissions } from '@/hooks/useMissions';
import { 
  addGameToFavorites, 
  removeGameFromFavorites, 
  isGameFavorited 
} from '@/services/likesService';
import { toast } from 'sonner';

interface GameFavoriteButtonProps {
  gameId: string;
  className?: string;
}

export function GameFavoriteButton({ gameId, className }: GameFavoriteButtonProps) {
  const { user } = useAuth();
  const { handleAddFavorite } = useMissions();
  const [isFavorited, setIsFavorited] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id && gameId) {
      loadFavoriteStatus();
    }
  }, [user?.id, gameId]);

  const loadFavoriteStatus = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const favorited = await isGameFavorited(gameId, user.id);
      setIsFavorited(favorited);
    } catch (error) {
      console.error('Error loading favorite status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user) {
      toast.error('You must be logged in to favorite games');
      return;
    }

    try {
      if (isFavorited) {
        const success = await removeGameFromFavorites(gameId, user.id);
        if (success) {
          setIsFavorited(false);
          toast.success('Game removed from favorites');
        } else {
          toast.error('Failed to remove game from favorites');
        }
      } else {
        const success = await addGameToFavorites(gameId, user.id);
        if (success) {
          setIsFavorited(true);
          toast.success('Game added to favorites!');
          
          // Complete the add favorite mission
          handleAddFavorite(gameId);
        } else {
          toast.error('Failed to add game to favorites');
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('An error occurred');
    }
  };

  if (loading) {
    return (
      <Button variant="outline" size="sm" disabled className={className}>
        <Star className="w-4 h-4 mr-2" />
        Loading...
      </Button>
    );
  }

        return (
          <Button
            variant={isFavorited ? "default" : "outline"}
            size="sm"
            onClick={handleToggleFavorite}
            className={`h-8 px-3 text-xs ${className}`}
          >
            <Star 
              className={`w-3 h-3 mr-1 ${isFavorited ? 'fill-current' : ''}`} 
            />
            {isFavorited ? 'Favorited' : 'Favorite'}
          </Button>
        );
}
